/**
 * Screen 2: Deal Card — /contact/:id
 *
 * Layout (refactored):
 * - StageCockpit (top, full-width) — главный пульт этапа
 * - Left column: Contact info + Lead context + Steps checklist + Timeline
 * - Right column: «Конфигурация и цена» (сводка + редактирование) + Payment 50/50 + Actions
 *
 * Весь существующий функционал сохранён:
 * - КП/PDF, Счёт/PDF (proposalApi, invoiceApi)
 * - Оплата 50/50 (paymentApi) — PaymentBlock
 * - Провижининг клиента (provisionApi) — в StageCockpit (onboarding)
 * - Ручной тест (testRunApi) — в StageCockpit (testing)
 * - Переходы стадий с гейт-локами — в StageCockpit + StageTransitionBlock
 * - Чек-лист шагов — в StageCockpit + отдельный блок
 * - Лента событий (getEvents)
 *
 * id param = dealId (формат deal-* или contact-*, принимаем как есть)
 */

import { useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  Lock,
  CheckCircle2,
  Circle,
  Loader2,
  ChevronRight,
  AlertTriangle,
  ChevronDown,
  ArrowRight,
  CheckCircle,
  Clock,
  GitBranch,
  MessageSquare,
  DollarSign,
  FileText,
  TestTube2,
  ServerCog,
  User,
  Wifi,
  Edit2,
  X,
} from 'lucide-react'
import {
  useDeal,
  useDealSteps,
  useDealEvents,
  usePipeline,
  useToggleStep,
  useDealAction,
  useMoveStage,
  dealKeys,
} from '@/hooks/useDeals'
import { useConfiguration } from '@/hooks/useConfiguration'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatRub, formatDate, formatRelativeTime } from '@/lib/utils'
import {
  calcPriceApi,
  invoiceApi,
  paymentApi,
  proposalApi,
  provisionApi,
  testRunApi,
} from '@/api/client'
import { StageCockpit } from '@/components/StageCockpit'
import { AgentEditor } from '@/components/AgentEditor'
import { ConfigurationForm } from '@/components/ConfigurationForm'
import type {
  CalcPriceResult,
  Deal,
  DealEvent,
  DealStep,
  GateKey,
  GateResult,
  InvoiceResult,
  ProposalResult,
  ProvisionClientResult,
  StageKey,
  TestRunResult,
} from '@/types'

// ---- Gate labels ----

const GATE_LABELS: Record<GateKey, string> = {
  gate_1: 'Ворота 1 — Созвон назначен',
  gate_2: 'Ворота 2 — Одобрение БЗ и промпт',
  gate_test: 'Тест-гейт ≥27/30',
  gate_3: 'Ворота 3 — Прод-запуск',
}

// ---- Gate badge ----

function GateBadge({ gateKey, result }: { gateKey: GateKey; result: GateResult }) {
  if (result === 'passed') {
    return (
      <Badge variant="gate_passed">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        {GATE_LABELS[gateKey]}
      </Badge>
    )
  }
  return (
    <div className="flex items-center gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
      <Lock className="h-4 w-4 text-slate-400 shrink-0" />
      <div>
        <p className="text-sm font-medium text-slate-600">{GATE_LABELS[gateKey]}</p>
        <p className="text-xs text-slate-500">
          {result === 'failed' ? 'Гейт не пройден — выполните условия' : 'Не пройден — заблокировано'}
        </p>
      </div>
    </div>
  )
}

// ---- Step row ----

function StepRow({
  step,
  onToggle,
  isToggling,
}: {
  step: DealStep
  onToggle: (stepKey: string, done: boolean) => void
  isToggling: boolean
}) {
  if (step.is_gate && step.gate_key) return null

  return (
    <div className="flex items-start gap-3 py-1.5">
      <button
        className="mt-0.5 shrink-0"
        onClick={() => onToggle(step.step_key, !step.done)}
        disabled={isToggling}
      >
        {step.done ? (
          <CheckCircle2 className="h-5 w-5 text-green-500" />
        ) : (
          <Circle className="h-5 w-5 text-slate-300 hover:text-slate-500 transition-colors" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <span className={`text-sm ${step.done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
          {step.label}
        </span>
        {step.done_at && step.done_by && (
          <p className="text-xs text-muted-foreground mt-0.5">
            {step.done_by} · {formatDate(step.done_at)}
          </p>
        )}
      </div>
    </div>
  )
}

// ---- Calc price result modal ----

function CalcPriceModal({
  result,
  onClose,
}: {
  result: CalcPriceResult
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-card rounded-xl border shadow-xl p-6 max-w-sm w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold mb-4">Расчёт стоимости</h3>

        <div className="space-y-3">
          {result.breakdown.map((item) => (
            <div key={item.label} className="flex justify-between text-sm">
              <span className="text-muted-foreground">{item.label}</span>
              <span className="font-medium">{formatRub(item.amount)}</span>
            </div>
          ))}

          <div className="border-t pt-3 space-y-2">
            <div className="flex justify-between text-sm font-semibold">
              <span>Сумма договора</span>
              <span>{formatRub(result.contract_total)}</span>
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Предоплата 50%</span>
              <span>{formatRub(result.first_tranche)}</span>
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Вторые 50%</span>
              <span>{formatRub(result.second_tranche)}</span>
            </div>
          </div>

          <div className="border-t pt-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Подписка от запуска</span>
              <span className="font-medium">{formatRub(result.subscription_base)}/мес</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              + брэкеты сверх 100 диалогов/мес
            </p>
          </div>
        </div>

        <Button className="w-full mt-5" size="sm" onClick={onClose}>
          Закрыть
        </Button>
      </div>
    </div>
  )
}

// ---- Proposal modal ----

function ProposalModal({
  result,
  dealId,
  onClose,
}: {
  result: ProposalResult
  dealId: string
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-card rounded-xl border shadow-xl p-6 max-w-2xl w-full mx-4 flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold">{result.title || 'Коммерческое предложение'}</h3>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="text-xs gap-1"
              onClick={() => window.open(proposalApi.getPdfUrl(dealId), '_blank')}
            >
              <FileText className="h-3 w-3" />
              Скачать PDF
            </Button>
            <button onClick={onClose} className="text-sm text-muted-foreground hover:text-foreground">✕</button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 rounded-md border bg-muted p-4">
          <pre className="whitespace-pre-wrap text-sm font-mono leading-relaxed">{result.md}</pre>
        </div>
        <Button className="mt-4" size="sm" variant="outline" onClick={onClose}>Закрыть</Button>
      </div>
    </div>
  )
}

// ---- Timeline event row ----

const EVENT_CONFIG: Record<string, { icon: React.ReactNode; label: (payload: Record<string, unknown>) => string }> = {
  claimed: {
    icon: <User className="h-4 w-4 text-blue-500" />,
    label: () => 'Лид взят в работу',
  },
  released: {
    icon: <User className="h-4 w-4 text-slate-400" />,
    label: () => 'Лид отпущен',
  },
  stage: {
    icon: <GitBranch className="h-4 w-4 text-purple-500" />,
    label: (p) => `Стадия изменена → ${String(p.to ?? '')}`,
  },
  price_calculated: {
    icon: <DollarSign className="h-4 w-4 text-green-500" />,
    label: (p) => `Цена рассчитана: ${p.total != null ? formatRub(Number(p.total)) : '—'}`,
  },
  proposal_generated: {
    icon: <FileText className="h-4 w-4 text-indigo-500" />,
    label: () => 'КП сформировано',
  },
  invoice_generated: {
    icon: <FileText className="h-4 w-4 text-orange-500" />,
    label: (p) => `Счёт #${String(p.number ?? '?')} выставлен`,
  },
  kb_gate_passed: {
    icon: <CheckCircle className="h-4 w-4 text-green-600" />,
    label: () => 'База знаний одобрена',
  },
  test_run_recorded: {
    icon: <TestTube2 className="h-4 w-4 text-amber-500" />,
    label: (p) => `Тест: ${String(p.score ?? '?')}/${String(p.max ?? 30)} — ${p.pass_ ? 'пройден' : 'не пройден'}`,
  },
  client_provisioned: {
    icon: <ServerCog className="h-4 w-4 text-teal-500" />,
    label: () => 'Кабинет клиента создан',
  },
  configuration_saved: {
    icon: <DollarSign className="h-4 w-4 text-green-500" />,
    label: (p) => `Конфигурация сохранена: договор ${p.contract_total != null ? formatRub(Number(p.contract_total)) : '—'}`,
  },
}

function TimelineEventRow({ event }: { event: DealEvent }) {
  const cfg = EVENT_CONFIG[event.type]
  const icon = cfg?.icon ?? <Clock className="h-4 w-4 text-slate-400" />
  const label = cfg ? cfg.label(event.payload) : event.type

  return (
    <div className="flex items-start gap-3 py-2">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground">{label}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
          <span>{formatRelativeTime(event.created_at)}</span>
          {event.by && <span>· {event.by}</span>}
        </div>
      </div>
    </div>
  )
}

// ---- Payment 50/50 block ----

function PaymentBlock({ deal, onRefresh }: { deal: Deal; onRefresh: () => void }) {
  const [marking, setMarking] = useState<'first' | 'second' | null>(null)
  const [invoiceLoading, setInvoiceLoading] = useState<1 | 2 | null>(null)
  const [invoiceResult1, setInvoiceResult1] = useState<InvoiceResult | null>(null)
  const [invoiceResult2, setInvoiceResult2] = useState<InvoiceResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

  if (!deal.value && deal.stage_key !== 'won' && deal.stage_key !== 'hypercare' && deal.stage_key !== 'active') {
    return null
  }

  const contractTotal = deal.value ?? 0
  const firstTranche = Math.round(contractTotal * 0.5)
  const secondTranche = contractTotal - firstTranche

  // Первый транш считаем по достигнутой сумме (paid_at — только для даты ниже),
  // чтобы статус совпадал со StageCockpit и не зависел от того, проставил ли бэк paid_at.
  const firstPaid = contractTotal > 0 && deal.paid_amount >= firstTranche
  const secondPaid = contractTotal > 0 && deal.paid_amount >= contractTotal

  async function handleMarkFirst() {
    setMarking('first')
    setError(null)
    try {
      await paymentApi.markFirst(deal.id, firstTranche)
      onRefresh()
    } catch {
      setError('Ошибка при сохранении')
    } finally {
      setMarking(null)
    }
  }

  async function handleMarkSecond() {
    setMarking('second')
    setError(null)
    try {
      await paymentApi.markSecond(deal.id, contractTotal)
      onRefresh()
    } catch {
      setError('Ошибка при сохранении')
    } finally {
      setMarking(null)
    }
  }

  async function handleGenerateInvoice(tranche: 1 | 2) {
    setInvoiceLoading(tranche)
    setError(null)
    try {
      let result: InvoiceResult
      if (USE_MOCK) {
        result = {
          number: `TB-${new Date().getFullYear()}-${tranche === 1 ? '001' : '002'}`,
          total: tranche === 1 ? firstTranche : secondTranche,
          md: `# Счёт TB-${new Date().getFullYear()}-${tranche === 1 ? '001' : '002'}\n\n**Клиент:** ${deal.contact?.display_name ?? deal.title}\n**Сумма:** ${formatRub(tranche === 1 ? firstTranche : secondTranche)}\n**Транш:** ${tranche === 1 ? '1-й (предоплата)' : '2-й (финальный)'}\n\n_Счёт действителен 5 рабочих дней_`,
        }
      } else {
        result = await invoiceApi.generate(deal.id, tranche)
      }
      if (tranche === 1) setInvoiceResult1(result)
      else setInvoiceResult2(result)
    } catch {
      setError(`Ошибка при формировании счёта (транш ${tranche})`)
    } finally {
      setInvoiceLoading(null)
    }
  }

  function renderInvoiceResult(result: InvoiceResult, tranche: 1 | 2) {
    return (
      <div className="mt-2 rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs space-y-1">
        <div className="font-medium text-blue-800">Счёт #{result.number} · {formatRub(result.total)}</div>
        <button
          className="text-blue-600 underline hover:text-blue-800"
          onClick={() => window.open(invoiceApi.getPdfUrl(deal.id, tranche), '_blank')}
        >
          Скачать PDF
        </button>
      </div>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Оплата 50/50</CardTitle>
        <p className="text-xs text-muted-foreground">
          База = сумма договора. Подписка — отдельно.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {contractTotal > 0 && (
          <div className="text-sm font-medium">
            Сумма договора: {formatRub(contractTotal)}
          </div>
        )}

        {/* First tranche */}
        <div className="rounded-md border p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Первые 50% — предоплата</p>
              <p className="text-xs text-muted-foreground">{formatRub(firstTranche)}</p>
            </div>
            <div className="flex flex-col items-end gap-1.5">
              {firstPaid ? (
                <div className="flex items-center gap-1.5 text-green-700 text-sm">
                  <CheckCircle className="h-4 w-4" />
                  {deal.paid_at ? `Получены ${formatDate(deal.paid_at)}` : 'Получены'}
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  disabled={marking === 'first'}
                  onClick={handleMarkFirst}
                >
                  {marking === 'first' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Пометить получены
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="text-xs gap-1"
                disabled={invoiceLoading === 1}
                onClick={() => handleGenerateInvoice(1)}
              >
                {invoiceLoading === 1 ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                Сформировать счёт (1-й транш)
              </Button>
            </div>
          </div>
          {invoiceResult1 && renderInvoiceResult(invoiceResult1, 1)}
        </div>

        {/* Second tranche */}
        <div className="rounded-md border p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Вторые 50% — после 2 нед. hypercare</p>
              <p className="text-xs text-muted-foreground">{formatRub(secondTranche)}</p>
              {deal.second_tranche_due && (
                <p className="text-xs text-amber-600 mt-0.5">
                  Срок: {formatDate(deal.second_tranche_due)}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1.5">
              {secondPaid ? (
                <div className="flex items-center gap-1.5 text-green-700 text-sm">
                  <CheckCircle className="h-4 w-4" />
                  Получены
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-xs"
                  disabled={marking === 'second' || !firstPaid}
                  onClick={handleMarkSecond}
                >
                  {marking === 'second' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Пометить получены
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="text-xs gap-1"
                disabled={invoiceLoading === 2 || !firstPaid}
                onClick={() => handleGenerateInvoice(2)}
              >
                {invoiceLoading === 2 ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                Сформировать счёт (2-й транш)
              </Button>
            </div>
          </div>
          {invoiceResult2 && renderInvoiceResult(invoiceResult2, 2)}
        </div>

        {error && <p className="text-xs text-destructive">{error}</p>}

        <p className="text-xs text-muted-foreground border-t pt-2">
          Подписка: 4 900 ₽/мес (100 разговоров включено) + брэкеты — отдельный счёт с запуска.
        </p>
      </CardContent>
    </Card>
  )
}

// ---- Stage transition block (right column, compact) ----

const PHASE_LABELS: Record<string, string> = {
  sales: 'Продажа',
  delivery: 'Внедрение',
  account: 'Сопровождение',
  terminal: 'Завершено',
}

function StageTransitionBlock({
  deal,
  pipeline,
  onMoved,
  onGateError,
}: {
  deal: Deal
  pipeline: ReturnType<typeof usePipeline>['data']
  onMoved: () => void
  onGateError: (msg: string) => void
}) {
  const moveStage = useMoveStage()
  const [transitioning, setTransitioning] = useState<StageKey | null>(null)

  const currentStage = pipeline?.stages.find((s) => s.key === deal.stage_key)

  if (!currentStage) return null

  async function handleMove(targetKey: StageKey) {
    setTransitioning(targetKey)
    try {
      await moveStage.mutateAsync({ dealId: deal.id, stageKey: targetKey })
      onMoved()
    } catch (err: unknown) {
      const apiErr = (err as { apiError?: { error: string; gate?: string; code?: string; detail?: string } }).apiError
      if (apiErr?.detail === 'client_not_provisioned') {
        onGateError('Сначала создайте кабинет клиента')
      } else if (apiErr?.detail === 'owner_bot_not_configured') {
        onGateError('Укажите токен бота и chat_id операторов')
      } else if (apiErr?.error === 'gate_blocked' || apiErr?.code === 'gate_blocked') {
        onGateError(`Гейт не пройден: ${apiErr.gate ?? 'условие перехода'} — выполните все шаги`)
      } else if (apiErr?.error === 'transition_not_allowed' || apiErr?.code === 'transition_not_allowed') {
        onGateError('Этот переход не разрешён')
      } else {
        onGateError('Ошибка при смене стадии')
      }
    } finally {
      setTransitioning(null)
    }
  }

  const allowedNext = currentStage.allowed_next
  const allowedBack = currentStage.allowed_back

  function isNextGateLocked(nextKey: StageKey): boolean {
    const nextStage = pipeline?.stages.find((s) => s.key === nextKey)
    if (!nextStage?.gate_key) return false
    const gate = deal.gates?.find((g) => g.gate_key === nextStage.gate_key)
    return !gate || gate.result !== 'passed'
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">
              {PHASE_LABELS[currentStage.phase ?? 'sales']}
            </p>
            <CardTitle className="text-base mt-0.5">{currentStage.name}</CardTitle>
          </div>
          <Badge variant={
            deal.status === 'frozen' ? 'urgency_yellow' :
            deal.status === 'lost' ? 'destructive' :
            deal.status === 'won' ? 'gate_passed' :
            'secondary'
          }>
            {deal.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {allowedNext.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
              <ArrowRight className="h-3 w-3" />
              Следующий шаг
            </p>
            <div className="flex flex-wrap gap-2">
              {allowedNext.map((nextKey) => {
                const nextStage = pipeline?.stages.find((s) => s.key === nextKey)
                const locked = isNextGateLocked(nextKey)
                const isLoading = transitioning === nextKey

                return (
                  <Button
                    key={nextKey}
                    size="sm"
                    variant={locked ? 'outline' : 'default'}
                    disabled={locked || isLoading}
                    onClick={() => !locked && handleMove(nextKey)}
                    className={`text-xs gap-1 ${locked ? 'opacity-60 cursor-not-allowed' : ''}`}
                    title={locked ? `Гейт не пройден: ${GATE_LABELS[nextStage?.gate_key ?? 'gate_1'] ?? ''}` : undefined}
                  >
                    {isLoading ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : locked ? (
                      <Lock className="h-3 w-3" />
                    ) : (
                      <ChevronRight className="h-3 w-3" />
                    )}
                    {nextStage?.name ?? nextKey}
                  </Button>
                )
              })}
            </div>
          </div>
        )}

        {allowedBack.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
              <ChevronDown className="h-3 w-3 rotate-180" />
              Вернуть назад
            </p>
            <div className="flex flex-wrap gap-2">
              {allowedBack.map((backKey) => {
                const backStage = pipeline?.stages.find((s) => s.key === backKey)
                const isLoading = transitioning === backKey

                return (
                  <Button
                    key={backKey}
                    size="sm"
                    variant="outline"
                    disabled={!!transitioning}
                    onClick={() => handleMove(backKey)}
                    className="text-xs text-muted-foreground"
                  >
                    {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                    {backStage?.name ?? backKey}
                  </Button>
                )
              })}
            </div>
          </div>
        )}

        {currentStage.gate_key && (
          <GateBadge
            gateKey={currentStage.gate_key}
            result={deal.gates?.find((g) => g.gate_key === currentStage.gate_key)?.result ?? 'pending'}
          />
        )}

        {allowedNext.map((nextKey) => {
          const nextStage = pipeline?.stages.find((s) => s.key === nextKey)
          if (!nextStage?.gate_key) return null
          const nextGate = deal.gates?.find((g) => g.gate_key === nextStage.gate_key)
          if (!nextGate || nextGate.result === 'passed') return null
          return (
            <div key={nextKey} className="flex items-center gap-2 text-xs text-slate-500">
              <AlertTriangle className="h-3 w-3 text-amber-500" />
              Переход в «{nextStage.name}» заблокирован — {GATE_LABELS[nextStage.gate_key]}
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

// ---- Toast ----

function ToastBanner({ message, type, onDismiss }: { message: string; type: 'error' | 'success'; onDismiss: () => void }) {
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-lg border px-4 py-3 shadow-lg ${type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}`}>
      <div>
        <p className={`text-sm font-medium ${type === 'error' ? 'text-red-700' : 'text-green-700'}`}>{message}</p>
      </div>
      <button onClick={onDismiss} className={`ml-2 text-xs ${type === 'error' ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'}`}>✕</button>
    </div>
  )
}

// ---- Source channel label ----

const SOURCE_LABELS: Record<string, string> = {
  web_form: 'Форма на сайте',
  telegram: 'Telegram',
  max: 'VK Max',
  avito: 'Авито',
  manual: 'Вручную',
}

// ---- Configuration summary block ----

function ConfigSummaryBlock({ dealId }: { dealId: string }) {
  const { data: configResp, isLoading } = useConfiguration(dealId)
  const [editing, setEditing] = useState(false)

  const calc = configResp?.calc_price
  const cfg = configResp?.configuration

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Конфигурация и цена</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Загрузка...
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Конфигурация и цена</CardTitle>
          <button
            className="text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setEditing((v) => !v)}
            title={editing ? 'Свернуть' : 'Изменить конфигурацию'}
          >
            {editing ? <X className="h-4 w-4" /> : <Edit2 className="h-4 w-4" />}
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!editing && calc ? (
          /* Сводка сохранённого расчёта */
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Сумма договора (50/50)</span>
              <span className="font-semibold">{formatRub(calc.contract_total)}</span>
            </div>
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Предоплата / остаток</span>
              <span>{formatRub(calc.first_tranche)} / {formatRub(calc.second_tranche)}</span>
            </div>
            {calc.upsells_total > 0 && (
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Апсейлы (отдельно)</span>
                <span>{formatRub(calc.upsells_total)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs text-muted-foreground border-t pt-2">
              <span>Подписка/мес (прогноз)</span>
              <span>{formatRub(calc.subscription_forecast.total)}</span>
            </div>
            {cfg && (
              <div className="text-xs text-muted-foreground">
                Каналы: {cfg.channels.join(', ') || '—'}
                {cfg.integrations.length > 0 && ` · Инт-ции: ${cfg.integrations.join(', ')}`}
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              className="text-xs gap-1 mt-1"
              onClick={() => setEditing(true)}
            >
              <Edit2 className="h-3 w-3" />
              Изменить конфигурацию
            </Button>
          </div>
        ) : !editing && !calc ? (
          /* Конфигурация ещё не сохранена */
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Конфигурация ещё не заполнена.</p>
            <Button
              size="sm"
              variant="outline"
              className="text-xs gap-1"
              onClick={() => setEditing(true)}
            >
              <Edit2 className="h-3 w-3" />
              Заполнить конфигурацию
            </Button>
          </div>
        ) : (
          /* Форма редактирования */
          <ConfigurationForm
            dealId={dealId}
            onSaved={() => setEditing(false)}
          />
        )}
      </CardContent>
    </Card>
  )
}

// ---- Main component ----

export function ContactPage() {
  const { id } = useParams({ from: '/layout/contact/$id' })
  const qc = useQueryClient()

  const dealId = id

  const { data: deal, isLoading: dealLoading, refetch: refetchDeal } = useDeal(dealId)
  const { data: steps = [], isLoading: stepsLoading } = useDealSteps(dealId)
  const { data: events = [], isLoading: eventsLoading } = useDealEvents(dealId)
  const { data: pipeline } = usePipeline()
  const toggleStep = useToggleStep()
  const actionMutation = useDealAction()
  const { data: configResp } = useConfiguration(dealId)

  const [calcResult, setCalcResult] = useState<CalcPriceResult | null>(null)
  const [calcLoading, setCalcLoading] = useState(false)
  const [proposalResult, setProposalResult] = useState<ProposalResult | null>(null)
  const [proposalLoading, setProposalLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' } | null>(null)

  // Feature E: Test result form state (kept for non-cockpit usage)
  const [testScore, setTestScore] = useState<string>('')
  const [testNotes, setTestNotes] = useState<string>('')
  const [testLoading, setTestLoading] = useState(false)
  const [testResult, setTestResult] = useState<TestRunResult | null>(null)

  // Feature F: Client provisioning form state (kept for non-cockpit usage)
  const [showProvisionForm, setShowProvisionForm] = useState(false)
  const [provisionLoading, setProvisionLoading] = useState(false)
  const [provisionResult, setProvisionResult] = useState<ProvisionClientResult | null>(null)
  const [provisionName, setProvisionName] = useState<string>('')
  const [provisionChannel, setProvisionChannel] = useState<'telegram' | 'max'>('telegram')
  const [provisionBotToken, setProvisionBotToken] = useState<string>('')
  const [provisionChatIds, setProvisionChatIds] = useState<string>('')

  const USE_MOCK = import.meta.env.VITE_USE_MOCK != null
  ? import.meta.env.VITE_USE_MOCK === 'true'
  : import.meta.env.DEV

  function showToast(message: string, type: 'error' | 'success' = 'error') {
    setToast({ message, type })
    setTimeout(() => setToast(null), 5000)
  }

  if (dealLoading || stepsLoading) {
    return <div className="p-4 text-muted-foreground">Загрузка карточки...</div>
  }
  if (!deal) {
    return <div className="p-4 text-destructive">Сделка не найдена (id: {id})</div>
  }

  const d = deal

  function getDefaultProvisionName() {
    const name = d.contact?.display_name ?? ''
    const niche = d.contact?.business_niche ?? ''
    return niche ? `${name} / ${niche}` : name
  }

  async function handleCalcPrice() {
    setCalcLoading(true)
    try {
      if (USE_MOCK) {
        const mockResult: CalcPriceResult = {
          contract_total: d.value ?? 80000,
          first_tranche: Math.round((d.value ?? 80000) * 0.5),
          second_tranche: Math.round((d.value ?? 80000) * 0.5),
          subscription_base: 4900,
          breakdown: [
            { label: 'Базовый пакет (1 канал)', amount: 60000 },
            { label: 'Доп. канал MAX', amount: d.value ? d.value - 60000 : 20000 },
          ],
        }
        setCalcResult(mockResult)
      } else {
        const result = await calcPriceApi.calc(d.id)
        setCalcResult(result)
      }
    } catch {
      showToast('Ошибка при расчёте цены')
    } finally {
      setCalcLoading(false)
    }
  }

  async function handleGenerateProposal() {
    setProposalLoading(true)
    try {
      if (USE_MOCK) {
        const mockProposal: ProposalResult = {
          title: `КП: ${d.contact?.display_name ?? d.title}`,
          md: `# Коммерческое предложение\n\n**Клиент:** ${d.contact?.display_name ?? d.title}\n**Ниша:** ${d.contact?.business_niche ?? '—'}\n\n## Что мы предлагаем\n\nЦифровой сотрудник на базе ИИ, который отвечает клиентам 24/7, записывает, консультирует и не устаёт.\n\n## Стоимость\n\n| Статья | Сумма |\n|--------|-------|\n| Внедрение (под ключ) | ${formatRub(d.value ?? 80000)} |\n| Подписка (от запуска) | 4 900 ₽/мес |\n\n## Сроки\n\n- Сборка: 2–3 недели\n- Тестирование: 1 неделя\n- Запуск: по результатам тест-гейта\n\n---\n_Предложение действует 14 дней_`,
        }
        setProposalResult(mockProposal)
      } else {
        const result = await proposalApi.generate(d.id)
        setProposalResult(result)
      }
    } catch {
      showToast('Ошибка при формировании КП')
    } finally {
      setProposalLoading(false)
    }
  }

  // Feature E: submit test run (legacy path in Actions block)
  async function handleTestRun(e: React.FormEvent) {
    e.preventDefault()
    const score = parseInt(testScore, 10)
    if (isNaN(score) || score < 0 || score > 30) {
      showToast('Введите балл от 0 до 30')
      return
    }
    setTestLoading(true)
    try {
      let result: TestRunResult
      if (USE_MOCK) {
        result = {
          test_run_id: `tr-mock-${Date.now()}`,
          pass_: score >= 27,
          score,
          max: 30,
          kb_content_hash: 'abc123',
        }
      } else {
        result = await testRunApi.record(d.id, { score, max: 30, notes: testNotes || undefined })
      }
      setTestResult(result)
      if (result.pass_) {
        showToast(`Тест пройден: ${score}/30`, 'success')
        qc.invalidateQueries({ queryKey: dealKeys.detail(d.id) })
      }
    } catch {
      showToast('Ошибка при записи теста')
    } finally {
      setTestLoading(false)
    }
  }

  // Feature F: provision client (legacy path in Actions block)
  async function handleProvision(e: React.FormEvent) {
    e.preventDefault()
    setProvisionLoading(true)
    try {
      const chatIds = provisionChatIds
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      let result: ProvisionClientResult
      if (USE_MOCK) {
        result = { client_tenant_id: `tenant-${Date.now()}`, created: true }
      } else {
        result = await provisionApi.provision(d.id, {
          name: provisionName || getDefaultProvisionName() || undefined,
          channel: provisionChannel,
          bot_token: provisionBotToken || undefined,
          operator_chat_ids: chatIds.length > 0 ? chatIds : undefined,
        })
      }
      setProvisionResult(result)
      setShowProvisionForm(false)
      showToast(`Кабинет клиента создан: ${result.client_tenant_id}`, 'success')
      refetchDeal()
    } catch {
      showToast('Ошибка при создании кабинета')
    } finally {
      setProvisionLoading(false)
    }
  }

  const doneSteps = steps.filter((s) => !s.is_gate && s.done).length
  const totalSteps = steps.filter((s) => !s.is_gate).length
  const nextStep = steps.find((s) => !s.is_gate && !s.done)

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* ===== COCKPIT — главный пульт этапа (full-width) ===== */}
      <StageCockpit
        deal={d}
        steps={steps}
        savedCalcPrice={configResp?.calc_price ?? null}
        onGateError={(msg) => showToast(msg)}
        onSuccess={(msg) => showToast(msg, 'success')}
        onRefresh={() => {
          refetchDeal()
          qc.invalidateQueries({ queryKey: dealKeys.detail(d.id) })
        }}
      />

      {/* ===== Агент клиента — инструкция и БЗ, доступны на любой стадии ===== */}
      <AgentEditor
        dealId={d.id}
        clientTenantId={d.client_tenant_id ?? null}
        onEdited={() => {
          refetchDeal()
          qc.invalidateQueries({ queryKey: dealKeys.detail(d.id) })
        }}
      />

      {/* ===== Two-column section ===== */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_400px] gap-6">
        {/* Left column: Contact info + Lead context + Steps + Timeline */}
        <div className="space-y-4">
          {/* Contact header card */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg">{d.contact?.display_name ?? d.title}</CardTitle>
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {d.contact?.phone && <p>{d.contact.phone}</p>}
                    {d.contact?.email && <p>{d.contact.email}</p>}
                    {d.contact?.business_niche && <p>{d.contact.business_niche}</p>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {d.is_repeat && <Badge variant="repeat">повторный</Badge>}
                  {d.value && (
                    <span className="text-sm font-semibold">{formatRub(d.value)}</span>
                  )}
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Lead context block */}
          {(d.contact?.business_niche || d.source_channel || d.contact?.phone || d.first_message) && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Контекст лида</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {d.contact?.business_niche && (
                  <div className="flex items-start gap-2">
                    <span className="text-muted-foreground shrink-0 w-16">Ниша</span>
                    <span>{d.contact.business_niche}</span>
                  </div>
                )}
                {d.source_channel && (
                  <div className="flex items-start gap-2">
                    <Wifi className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <span className="text-muted-foreground shrink-0 w-12">Канал</span>
                    <span>{SOURCE_LABELS[d.source_channel] ?? d.source_channel}</span>
                  </div>
                )}
                {d.contact?.phone && (
                  <div className="flex items-start gap-2">
                    <span className="text-muted-foreground shrink-0 w-16">Телефон</span>
                    <span>{d.contact.phone}</span>
                  </div>
                )}
                {d.first_message && (
                  <div className="flex items-start gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <span className="text-muted-foreground shrink-0 w-12">Боль</span>
                    <span className="italic text-foreground">{d.first_message}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Steps checklist */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Задачи стадии</CardTitle>
                {totalSteps > 0 && (
                  <span className="text-xs text-muted-foreground">{doneSteps}/{totalSteps}</span>
                )}
              </div>
              {nextStep && (
                <div className="flex items-center gap-1.5 mt-1 text-xs font-medium text-amber-700 bg-amber-50 rounded px-2 py-1">
                  <ChevronRight className="h-3 w-3" />
                  Следующий шаг: {nextStep.label}
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-1">
              {steps.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Нет шагов для этой стадии</p>
              ) : (
                <>
                  {steps.map((step) => (
                    <StepRow
                      key={step.step_key}
                      step={step}
                      onToggle={(stepKey, done) =>
                        toggleStep.mutate({ dealId: d.id, stepKey, done })
                      }
                      isToggling={toggleStep.isPending}
                    />
                  ))}
                  {steps.filter((s) => s.is_gate && s.gate_key).map((step) => (
                    <div key={step.step_key} className="mt-3">
                      <GateBadge
                        gateKey={step.gate_key!}
                        result={d.gates?.find((g) => g.gate_key === step.gate_key)?.result ?? 'pending'}
                      />
                    </div>
                  ))}
                </>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Лента событий</CardTitle>
            </CardHeader>
            <CardContent>
              {eventsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Загрузка...
                </div>
              ) : events.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  Событий пока нет
                </p>
              ) : (
                <div className="divide-y">
                  {events.map((event: DealEvent, idx: number) => (
                    <TimelineEventRow key={`${event.type}-${event.created_at}-${idx}`} event={event} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Stage transitions (compact) */}
          <StageTransitionBlock
            deal={d}
            pipeline={pipeline}
            onMoved={() => refetchDeal()}
            onGateError={(msg) => showToast(msg)}
          />

          {/* Configuration & Price summary */}
          <ConfigSummaryBlock dealId={d.id} />

          {/* Payment 50/50 */}
          <PaymentBlock deal={d} onRefresh={() => refetchDeal()} />

          {/* Actions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Действия</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {/* Calc price (legacy quick-calc) */}
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                disabled={calcLoading}
                onClick={handleCalcPrice}
              >
                {calcLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Рассчитать цену
              </Button>

              {/* Gen proposal */}
              <Button
                variant="outline"
                size="sm"
                className="text-xs gap-1"
                disabled={proposalLoading}
                onClick={handleGenerateProposal}
              >
                {proposalLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
                Сформировать КП
              </Button>

              {/* Feature F: Client provisioning (onboarding stage) */}
              {d.stage_key === 'onboarding' && (
                <div className="w-full space-y-2 border-t pt-3 mt-1">
                  {provisionResult ? (
                    <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-800">
                      <CheckCircle className="h-3 w-3 inline mr-1" />
                      Кабинет клиента создан: <strong>{provisionResult.client_tenant_id}</strong>
                    </div>
                  ) : null}
                  {!showProvisionForm ? (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs gap-1"
                      onClick={() => {
                        setProvisionName(getDefaultProvisionName())
                        setShowProvisionForm(true)
                      }}
                    >
                      <ServerCog className="h-3 w-3" />
                      Создать кабинет клиента
                    </Button>
                  ) : (
                    <form onSubmit={handleProvision} className="space-y-2 p-3 rounded-md border bg-muted/40">
                      <p className="text-xs font-medium">Создать кабинет клиента</p>
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Название кабинета</label>
                        <input
                          type="text"
                          className="w-full text-xs rounded border px-2 py-1.5 bg-background"
                          value={provisionName}
                          onChange={(e) => setProvisionName(e.target.value)}
                          placeholder={getDefaultProvisionName()}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Канал</label>
                        <select
                          className="w-full text-xs rounded border px-2 py-1.5 bg-background"
                          value={provisionChannel}
                          onChange={(e) => setProvisionChannel(e.target.value as 'telegram' | 'max')}
                        >
                          <option value="telegram">Telegram</option>
                          <option value="max">VK Max</option>
                        </select>
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Bot token (опционально)</label>
                        <input
                          type="text"
                          className="w-full text-xs rounded border px-2 py-1.5 bg-background font-mono"
                          value={provisionBotToken}
                          onChange={(e) => setProvisionBotToken(e.target.value)}
                          placeholder="1234567890:ABC..."
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs text-muted-foreground">Chat IDs операторов (через запятую)</label>
                        <input
                          type="text"
                          className="w-full text-xs rounded border px-2 py-1.5 bg-background font-mono"
                          value={provisionChatIds}
                          onChange={(e) => setProvisionChatIds(e.target.value)}
                          placeholder="-100123456789, 987654321"
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        Токен и chat_id нужны до Ворот 3 (запуск)
                      </p>
                      <div className="flex gap-2">
                        <Button type="submit" size="sm" className="text-xs" disabled={provisionLoading}>
                          {provisionLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                          Создать
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          onClick={() => setShowProvisionForm(false)}
                        >
                          Отмена
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              )}

              {/* Feature E: Test result form (testing stage) */}
              {d.stage_key === 'testing' && (
                <div className="w-full space-y-2 border-t pt-3 mt-1">
                  <p className="text-xs font-medium text-foreground">Результат теста</p>
                  <form onSubmit={handleTestRun} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-muted-foreground w-16 shrink-0">Балл (0–30)</label>
                      <input
                        type="number"
                        min={0}
                        max={30}
                        className="w-20 text-xs rounded border px-2 py-1.5 bg-background"
                        value={testScore}
                        onChange={(e) => setTestScore(e.target.value)}
                        placeholder="27"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Заметки (опционально)</label>
                      <textarea
                        className="w-full text-xs rounded border px-2 py-1.5 bg-background resize-none"
                        rows={2}
                        value={testNotes}
                        onChange={(e) => setTestNotes(e.target.value)}
                        placeholder="Результаты прогона..."
                      />
                    </div>
                    <Button type="submit" size="sm" variant="outline" className="text-xs" disabled={testLoading}>
                      {testLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <TestTube2 className="h-3 w-3 mr-1" />}
                      Записать результат
                    </Button>
                  </form>
                  {testResult && (
                    <div className={`rounded-md border px-3 py-2 text-xs ${testResult.pass_ ? 'bg-green-50 border-green-200 text-green-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                      {testResult.pass_
                        ? `✅ тест-гейт пройден (${testResult.score}/${testResult.max})`
                        : `⚠️ ниже порога — нужен откат на сборку (${testResult.score}/${testResult.max})`}
                    </div>
                  )}
                </div>
              )}

              {/* Danger zone */}
              <div className="w-full border-t pt-3 mt-1 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs text-amber-600 border-amber-200"
                  onClick={() => actionMutation.mutate({ dealId: d.id, kind: 'tenant_pause', label: 'Заморозить' })}
                >
                  Заморозить
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs text-destructive border-red-200"
                  onClick={() => {
                    if (confirm('Закрыть сделку как проигранную?')) {
                      actionMutation.mutate({ dealId: d.id, kind: 'release_owner', label: 'Закрыть как проиграно' })
                    }
                  }}
                >
                  Закрыть как проиграно
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modals */}
      {proposalResult && (
        <ProposalModal
          result={proposalResult}
          dealId={d.id}
          onClose={() => setProposalResult(null)}
        />
      )}

      {calcResult && (
        <CalcPriceModal result={calcResult} onClose={() => setCalcResult(null)} />
      )}

      {toast && (
        <ToastBanner
          message={toast.message}
          type={toast.type}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  )
}
